import { Box, Chip, Stack, Typography } from '@mui/material';
import type { SetURLSearchParams } from 'react-router-dom';

import { MULTI_VALUE_KEYS, type MultiValueKey, removeFilter } from '../../utils/searchUrl';
import { toSentenceCase } from '../../utils/labels';

interface ActiveFilterChipsProps {
  /** Current URL search params (source of truth). */
  searchParams: URLSearchParams;
  /** Setter from react-router's `useSearchParams`. */
  setSearchParams: SetURLSearchParams;
}

interface ActiveChip {
  /** Stable React key. */
  id: string;
  /** Visible chip label. */
  label: string;
  /** Param key to remove. */
  paramKey: string;
  /** For multi-value keys: the specific value to drop. */
  paramValue?: string;
}

const PRETTY_KEY: Record<string, string> = {
  q: 'Query',
  caliber: 'Caliber',
  ammoType: 'Ammo type',
  brand: 'Brand',
  productLine: 'Product line',
  bulletType: 'Bullet type',
  bulletWeightGrains: 'Grain',
  caseMaterial: 'Case material',
  retailerId: 'Retailer',
  useCases: 'Use case',
  minPricePerRound: 'Min CPR',
  maxPricePerRound: 'Max CPR',
  minRoundCount: 'Min rounds',
  stockStatus: 'Stock',
  inStockOnly: 'In-stock only',
  excludeRemanufactured: 'Exclude reman.',
  lotNumber: 'Lot #',
};

/** Build the list of active-filter chips from the current URL params. */
function collectChips(searchParams: URLSearchParams): ActiveChip[] {
  const out: ActiveChip[] = [];

  const q = searchParams.get('q');
  if (q) {
    out.push({ id: `q:${q}`, label: q, paramKey: 'q' });
  }

  for (const key of MULTI_VALUE_KEYS as readonly MultiValueKey[]) {
    const values = searchParams.getAll(key);
    for (const value of values) {
      if (!value) continue;
      // Grain values are numeric — append "gr" for readability so the
      // chip matches the sidebar's display ("115 gr") rather than
      // showing a bare integer.
      const displayValue =
        key === 'bulletWeightGrains'
          ? `${value} gr`
          : key === 'useCases' || key === 'ammoType'
            ? toSentenceCase(value) // #112/#108: match the facet labels' sentence case
            : value;
      out.push({
        id: `${key}:${value}`,
        label: `${PRETTY_KEY[key] ?? key}: ${displayValue}`,
        paramKey: key,
        paramValue: value,
      });
    }
  }

  for (const key of ['minPricePerRound', 'maxPricePerRound', 'minRoundCount'] as const) {
    const value = searchParams.get(key);
    if (value) {
      out.push({
        id: `${key}:${value}`,
        label: `${PRETTY_KEY[key]}: ${value}`,
        paramKey: key,
      });
    }
  }

  const stockStatus = searchParams.get('stockStatus');
  if (stockStatus) {
    out.push({
      id: `stockStatus:${stockStatus}`,
      label: `Stock: ${stockStatus.replace(/_/g, ' ')}`,
      paramKey: 'stockStatus',
    });
  }

  if (searchParams.get('inStockOnly') === 'true') {
    out.push({ id: 'inStockOnly', label: 'In-stock only', paramKey: 'inStockOnly' });
  }
  if (searchParams.get('excludeRemanufactured') === 'true') {
    out.push({
      id: 'excludeRemanufactured',
      label: 'Exclude remanufactured',
      paramKey: 'excludeRemanufactured',
    });
  }

  const lot = searchParams.get('lotNumber');
  if (lot) {
    out.push({ id: `lotNumber:${lot}`, label: `Lot #: ${lot}`, paramKey: 'lotNumber' });
  }

  return out;
}

/**
 * Renders one removable `<Chip>` per active filter. Removing a chip
 * deletes that param from the URL (or, for multi-value keys, only the
 * matching value).
 */
export function ActiveFilterChips({ searchParams, setSearchParams }: ActiveFilterChipsProps) {
  const chips = collectChips(searchParams);
  if (chips.length === 0) return null;

  function onDelete(chip: ActiveChip) {
    const next = removeFilter(searchParams, chip.paramKey, chip.paramValue);
    setSearchParams(next);
  }

  return (
    <Box
      component="section"
      aria-label="Active filters"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
        Active filters:
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {chips.map((chip) => (
          <Chip
            key={chip.id}
            label={chip.label}
            onDelete={() => onDelete(chip)}
            size="small"
            data-testid={`active-filter-chip-${chip.paramKey}`}
          />
        ))}
      </Stack>
    </Box>
  );
}

export default ActiveFilterChips;
